'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/organization-context';
import { apiClient } from '@/lib/api-client';
import {
  PetrohqApiResponseSelfSalesResponse,
  SelfSalesAggregationDto,
  SelfSalesDetailsDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function SelfSales() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [aggregationData, setAggregationData] = useState<
    SelfSalesAggregationDto[]
  >([]);
  const [detailsData, setDetailsData] = useState<SelfSalesDetailsDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    aggregation: false,
    details: false,
  });

  // 페이징 관련 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalElements, setTotalElements] = useState(0);
  const [isLast, setIsLast] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // 데이터 가져오기 함수
  const fetchSelfSalesData = useCallback(
    async (append = false, pageNumber = currentPage) => {
      // API 호출 시점에 현재 필터 상태 읽기
      const filters = useFilterStore.getState();

      if (!filters.dateRange?.start || !filters.dateRange?.end) {
        return;
      }

      setIsLoading(true);
      try {
        const storeCodeList =
          filters.store.length > 0 ? filters.store : getAllStores();

        // 필터 스토어를 사용하여 필터 파라미터 자동 생성 (페이지별 특수 처리 포함)
        const params = filters.buildApiParamsWithExtensions(
          'selfsales',
          storeCodeList
        );

        params.append('page', pageNumber.toString());
        params.append('size', pageSize.toString());

        // 단일 API 호출로 모든 데이터 가져오기
        const response = await apiClient.get('sales/selfsales', {
          searchParams: params,
        });
        const data =
          (await response.json()) as PetrohqApiResponseSelfSalesResponse;

        if (data.result) {
          // 집계 데이터 설정 (append 모드가 아닐 때만)
          if (!append && data.result.selfSalesAggregation) {
            setAggregationData(
              data.result.selfSalesAggregation as SelfSalesAggregationDto[]
            );
          }

          // 상세 데이터 설정
          if (data.result.selfSalesDetails) {
            const newData = data.result.selfSalesDetails;

            if (append) {
              console.log(
                `페이지 ${pageNumber} 데이터 ${newData.page?.number}건 추가`
              );
              setDetailsData(prev => {
                const combined = [...prev, ...(newData.content || [])];
                console.log(`총 데이터: ${combined.length}건`);
                return combined;
              });
            } else {
              console.log(`첫 페이지 데이터 ${newData.content?.length}건 로드`);
              setDetailsData(newData.content || []);
            }

            // 페이징 정보 업데이트 (API 응답에 페이징 정보가 있다면 사용, 없으면 기본값)
            setTotalElements(newData.page?.totalElements || 0);
            setIsLast(
              newData.page?.number === (newData.page?.totalPages || 0) - 1
            );
            setHasMore(
              (newData.page?.number || 0) < (newData.page?.totalPages || 0) - 1
            );
          }
        }
      } catch (error) {
        console.error('Self sales data fetch error:', error);
        toast({
          title: '조회 실패',
          description: 'SELF 판매현황 데이터를 불러오는 중 오류가 발생했습니다',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [getAllStores, toast]
  );

  // 초기 데이터 로드 및 필터 변경 시 리로드
  useEffect(() => {
    // 필터 변경 시 페이지 리셋
    setCurrentPage(0);
    setDetailsData([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
    fetchSelfSalesData();
  }, [fetchSelfSalesData]);

  // 그리드 데이터 업데이트
  useEffect(() => {
    if (aggregationData.length > 0) {
      console.log('집계 데이터 업데이트:', aggregationData.length);
    }
    if (detailsData.length > 0) {
      console.log('상세 데이터 업데이트:', detailsData.length);
    }
  }, [aggregationData, detailsData]);

  // 무한 스크롤 - 더 많은 데이터 로드
  const loadMoreData = useCallback(() => {
    console.log(
      'loadMoreData 호출 - currentPage:',
      currentPage,
      'hasMore:',
      hasMore,
      'isLoading:',
      isLoading
    );
    if (!isLoading && !isLast && hasMore) {
      const nextPage = currentPage + 1;
      console.log('다음 페이지 로드:', nextPage);
      setCurrentPage(nextPage);
      fetchSelfSalesData(true, nextPage);
    }
  }, [isLoading, hasMore, currentPage, fetchSelfSalesData]);

  // 조회 핸들러
  const handleSearch = () => {
    setCurrentPage(0);
    setDetailsData([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
    fetchSelfSalesData();
  };

  // 페이지 크기 변경
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setDetailsData([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
  };

  // 확장/축소 핸들러
  const handleExpandChange = (gridType: string) => (isExpanded: boolean) => {
    setExpanded(prev => ({
      ...prev,
      [gridType]: isExpanded,
    }));
  };

  return (
    <div>
      {/* SELF 판매현황 집계 */}
      <ExpandableCard
        title='SELF 판매현황 집계'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('selfsales')} />
        }
        pageHeader={
          <PageHeader onSearch={handleSearch} isLoading={isLoading} />
        }
        onExpandChange={handleExpandChange('aggregation')}
      >
        <div
          className='border rounded-lg'
          style={{ height: expanded.aggregation ? '95%' : '230px' }}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
              }}
              rows={aggregationData}
              layout={[
                {
                  name: '합계',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['totalCnt', 'totalAmt'],
                },
                {
                  name: 'SELF현금',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['selfCashCnt', 'selfCashAmt'],
                },
                {
                  name: 'SELF신용',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['selfCreditCnt', 'selfCreditAmt'],
                },
                {
                  name: '선수카드',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['prepayCnt', 'prepayAmt'],
                },
                {
                  name: '외상카드',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['onCreditCnt', 'onCreditAmt'],
                },
                {
                  name: '기타',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['etcCnt', 'etcAmt'],
                },
              ]}
            >
              <RGDataField
                fieldName={'selfId'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'totalCnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'totalAmt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'selfCashCnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'selfCashAmt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'selfCreditCnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'selfCreditAmt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'prepayCnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'prepayAmt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'onCreditCnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'onCreditAmt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'etcCnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'etcAmt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataColumn
                fieldName={'selfId'}
                header={{ text: 'SELFID' }}
                width={80}
                footer={{ text: '총계' }}
              />
              <RGDataColumn
                fieldName={'totalCnt'}
                header={{ text: '건수' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'totalAmt'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'selfCashCnt'}
                header={{ text: '건수' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'selfCashAmt'}
                header={{ text: '금액' }}
                numberFormat={'#,##0.000'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0.000' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0.000' }}
              />
              <RGDataColumn
                fieldName={'selfCreditCnt'}
                header={{ text: '건수' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'selfCreditAmt'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'prepayCnt'}
                header={{ text: '건수' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'prepayAmt'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'onCreditCnt'}
                header={{ text: '건수' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'onCreditAmt'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'etcCnt'}
                header={{ text: '건수' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'etcAmt'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
            </RealGridReact>
          </div>
        </div>
      </ExpandableCard>

      {/* SELF 판매현황 상세 */}
      <ExpandableCard
        title='SELF 판매현황'
        onExpandChange={handleExpandChange('details')}
      >
        <div
          className='border rounded-lg'
          style={{ height: expanded.details ? '95%' : '720px' }}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
              }}
              rows={detailsData}
              onScrollToBottom={loadMoreData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'seqNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'selfNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'pumpNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'typeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stype'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stTime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stQty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'stAmount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'saleokFalg'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'qty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'janAmount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'creditNum'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'approvalNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardMaiibCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardCompanyNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'bfErrmsg'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stCancelApflag'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stAmount2'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'stApprovalNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stCancelAptime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'afErrmsg'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'edApflag'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'edApprovalNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'edAptime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'bonusNum'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardNum'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'carNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'errProcess'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'errCancelAptime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'errMemo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataColumn fieldName={'storeCd'} header={{ text: '매장' }} />
              <RGDataColumn
                fieldName={'closedt'}
                header={{ text: '마감일자' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'closecnt'}
                header={{ text: '차수' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'seqNo'}
                header={{ text: 'SEQ No.' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'selfNo'}
                header={{ text: 'SELF' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'pumpNo'}
                header={{ text: '노즐' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종명' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'typeName'}
                header={{ text: '판매구분' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'stype'}
                header={{ text: 'SELF구분' }}
                width={80}
                lookupDisplay={true}
                values={['0', '1', '2']}
                labels={['0', '1', '2']}
              />
              <RGDataColumn
                fieldName={'stTime'}
                header={{ text: '허가시간' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'stQty'}
                header={{ text: '수량' }}
                width={70}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'stAmount'}
                header={{ text: '허가금액' }}
                width={90}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'stime'}
                header={{ text: '주유시간' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'qty'}
                header={{ text: '수량' }}
                width={70}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'cost'}
                header={{ text: '단가' }}
                width={70}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '주유금액' }}
                width={90}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'janAmount'}
                header={{ text: '차이액' }}
                width={80}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'creditNum'}
                header={{ text: '카드번호' }}
                width={110}
              />
              <RGDataColumn
                fieldName={'approvalNo'}
                header={{ text: '승인번호' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'cardMaiibCd'}
                header={{ text: '매입사' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'cardCompanyNo'}
                header={{ text: '매입사명' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'bfErrmsg'}
                header={{ text: '원거래메시지' }}
                width={100}
              />
              <RGDataColumn
                fieldName={'stCancelApflag'}
                header={{ text: '원거래취소' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'stAmount2'}
                header={{ text: '원거래금액' }}
                width={90}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'stApprovalNo'}
                header={{ text: '원거래승인번호' }}
                width={110}
              />
              <RGDataColumn
                fieldName={'stCancelAptime'}
                header={{ text: '원거래시간' }}
                width={100}
              />
              <RGDataColumn
                fieldName={'afErrmsg'}
                header={{ text: '확정승인 메시지' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'edApflag'}
                header={{ text: '확정승인' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'edApprovalNo'}
                header={{ text: '승인번호' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'edAptime'}
                header={{ text: '확정승인시간' }}
                width={110}
              />
              <RGDataColumn
                fieldName={'bonusNum'}
                header={{ text: '보너스카드' }}
                width={110}
              />
              <RGDataColumn
                fieldName={'cardNum'}
                header={{ text: '고객카드' }}
                width={110}
              />
              <RGDataColumn
                fieldName={'dealCode'}
                header={{ text: '거래처 코드' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '거래처명' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'carNo'}
                header={{ text: '차량번호' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'errProcess'}
                header={{ text: '장애처리' }}
                width={80}
                lookupDisplay={true}
                values={['0', '1', '2']}
                labels={['처리', '미처리', '보류']}
              />
              <RGDataColumn
                fieldName={'errCancelAptime'}
                header={{ text: '장애처리일시' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'errMemo'}
                header={{ text: '장애처리(MEMO)' }}
                width={120}
              />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              현재 {detailsData.length}건 / 총 {totalElements.toLocaleString()}
              건
              {currentPage > 0 && (
                <span className='ml-2 text-xs bg-gray-100 px-2 py-1 rounded'>
                  {currentPage + 1}페이지까지 로드됨
                </span>
              )}
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
            {hasMore && !isLoading && (
              <span className='text-sm text-orange-600'>
                스크롤하여 더 보기
              </span>
            )}
            {!hasMore && totalElements > 0 && (
              <span className='text-sm text-green-600'>
                모든 데이터 로드 완료
              </span>
            )}
          </div>
          <div className='flex items-center space-x-2'>
            {hasMore && (
              <Button
                variant='outline'
                size='sm'
                onClick={loadMoreData}
                disabled={isLoading}
              >
                더 보기
              </Button>
            )}
            <div className='border-l pl-2 flex items-center space-x-2'>
              <span className='text-sm text-muted-foreground'>
                페이지 크기:
              </span>
              {[50, 100, 500].map(size => (
                <Button
                  key={size}
                  variant={pageSize === size ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => handlePageSizeChange(size)}
                  disabled={isLoading}
                >
                  {size}
                </Button>
              ))}
              <Button
                variant={pageSize >= 1000 ? 'default' : 'outline'}
                size='sm'
                onClick={() => handlePageSizeChange(totalElements || 50)}
                disabled={isLoading}
              >
                전체
              </Button>
            </div>
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
